import { useEffect, useRef, useState, type CSSProperties, type ImgHTMLAttributes } from "react";

type Tone = "light" | "dark";

interface Props extends Omit<ImgHTMLAttributes<HTMLImageElement>, "onLoad" | "onError"> {
  src: string;
  alt: string;
  /** Skeleton + fallback tone. Default light. */
  tone?: Tone;
  /** Extra class applied to the <img> element */
  imgClassName?: string;
  /** Extra style applied to the <img> element */
  imgStyle?: CSSProperties;
  /** eager for above-the-fold hero-ish media */
  eager?: boolean;
}

/**
 * SmartImage — decode-aware image with skeleton, crossfade, and stable geometry.
 * MUST be placed inside a parent with explicit width AND height. It fills its
 * parent absolutely so the container's ratio is preserved.
 */
export default function SmartImage({
  src,
  alt,
  tone = "light",
  imgClassName = "",
  imgStyle,
  eager = false,
  ...rest
}: Props) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [ready, setReady] = useState(false);
  const [errored, setErrored] = useState(false);
  const lastGoodRef = useRef<string>("");

  // Handle src changes with preload + decode → crossfade
  useEffect(() => {
    let cancelled = false;
    if (!src) return;

    // First load: skeleton path via the on-screen <img>
    if (!lastGoodRef.current) {
      setReady(false);
      setErrored(false);
      setCurrentSrc(src);
      return;
    }

    // Subsequent swap (e.g. colorway change): keep current image visible,
    // preload new one, then swap.
    const preload = new Image();
    preload.decoding = "async";
    preload.src = src;
    const done = () => {
      if (cancelled) return;
      setCurrentSrc(src);
      setReady(true);
      setErrored(false);
      lastGoodRef.current = src;
    };
    const fail = () => {
      if (cancelled) return;
      // Keep the previous valid image visible; do not blank out.
    };
    if ("decode" in preload) {
      preload.decode().then(done).catch(() => {
        if (preload.complete && preload.naturalWidth > 0) done();
        else fail();
      });
    } else {
      preload.onload = done;
      preload.onerror = fail;
    }
    return () => { cancelled = true; };
  }, [src]);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const finish = () => {
      setReady(true);
      setErrored(false);
      lastGoodRef.current = currentSrc;
    };
    if ("decode" in img) {
      img.decode().then(finish).catch(finish);
    } else {
      finish();
    }
  };

  const handleError = () => {
    if (lastGoodRef.current) {
      // fall back to previous valid image silently
      return;
    }
    setErrored(true);
    setReady(true); // hide skeleton
  };

  const showSkeleton = !ready && !errored;

  return (
    <div className="si-wrap">
      {showSkeleton && (
        <div className={`si-skeleton ${tone === "dark" ? "is-dark" : ""}`} aria-hidden="true" />
      )}
      {!errored && (
        <img
          {...rest}
          src={currentSrc}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          loading={eager ? "eager" : rest.loading}
          decoding="async"
          className={`si-img ${ready ? "is-ready" : ""} ${imgClassName}`}
          style={imgStyle}
        />
      )}
      {errored && (
        <div
          className="absolute inset-0"
          style={{ background: tone === "dark" ? "#242522" : "#F1F1EE" }}
          aria-label={alt}
          role="img"
        />
      )}
    </div>
  );
}